import { objectReplace } from '@scalar-org/helpers/objects'
import { safeJSONParse } from '@/helpers/safe-parse'
import type { HastNode, HastRoot } from '@/types'
import type { SidebarItem } from '@scalar-org/entities/project'
import { EditorClasses } from '@scalar-org/guide'

import { customPageLinkStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomPageLink'
import { customPageTitleStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomPageTitle'
import { customTabsStatic } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomTabs'

import rehypeParse from 'rehype-parse'
import { unified, type Plugin } from 'unified'
import { visit } from 'unist-util-visit'

import { customComponentRender, componentNames } from '@/elements'

/**
 * Unified plugin to substitute placeholder html tags into their correct
 * content provided by the the associated "render" function
 */
export const customComponentsPlugin: (
  sidebarItems: Partial<Record<string, SidebarItem>>,
  pageTitle: string,
  pageDescription?: string,
) => Plugin =
  (
    sidebarItems: Partial<Record<string, SidebarItem>>,
    pageTitle: string,
    pageDescription?: string,
  ): Plugin<[], any, any> =>
  () => {
    return async (tree) => {
      /** We store a list of all the custom elements that must be rendered out into actual HTML */
      const customElements: {
        node: HastNode
        props: Record<string, any>
        // children: HastNode[]
        render: (props: Record<string, any>) => Promise<string>
      }[] = []

      // Visit each top level node
      visit(tree, (node: HastNode | HastRoot) => {
        // If the node is an element type we check if it is a text extension placeholder tag
        if (node.type === 'element' && componentNames.includes(node.tagName)) {
          // TODO: This is where we are about to render the elements from the properties
          // at this points we should ensure we run the attributeValidation here
          // If it fails console log and do not push the render function to the array
          const attrs = safeJSONParse(node.properties?.dataAttrs)

          // For page title we have to manually inject the data
          if (node.tagName === customPageTitleStatic.name) {
            // Fallback to attrs which are provided by markdown rendering
            attrs.title = pageTitle || attrs.title || ''
            attrs.description = pageDescription || attrs.description || ''
          }

          // For page link we have to manually inject the sidebar
          if (node.tagName === customPageLinkStatic.name) {
            attrs.item = sidebarItems[(attrs.item as any)?.uid]
          }

          // Custom Tabs require the child attributes to properly construct the tab selector
          if (node.tagName === customTabsStatic.name) {
            attrs.children = node.children.map((child) =>
              safeJSONParse(child.properties?.dataAttrs),
            )
          }

          customElements.push({
            node,
            props: attrs,
            render: customComponentRender[node.tagName],
          })
        }
      })

      /**
       * The HTML render functions for custom elements are asynchronous and must
       * be awaited outside of the visit function
       *
       * This process must also be run after the original function as nodes may be
       * mutated to allow substituting child elements back into a custom rendered element
       *
       * The process is:
       * 1. Render the raw HTML string
       * 2. Parse the new HTML string into a HAST tree fragment
       */
      await Promise.all(
        customElements.map(async (el) => {
          /** HTML string created by the custom component plugin */
          const elementHtmlString = await el.render(el.props)

          /** HTML string element parsed into a HAST tree */
          const hastRoot = unified()
            .use(rehypeParse, { fragment: true })
            .parse(elementHtmlString)

          if (el.node.type !== 'element') {
            console.warn(
              `Unexpected non-element node returned from custom component ${el.node}.`,
            )
            return
          }

          if (hastRoot.children.length > 1) {
            console.warn(
              `Scalar custom element ${el.node.tagName} returned multiple root nodes. Only the first child will be used.`,
            )
          }

          /**
           * The HAST tree will have all root nodes generated by the HTML string as children of the root
           * We assume ALL render functions return only a single top level node.
           * ex. `<div>...some html content...</div>` and never `<h1>Top Level 1</h1><h2>Top Level 2</h2>`
           *
           * If this is the case when we just grab the first child of the root as the new subtree
           */
          const ssgSearchRoot = hastRoot.children[0]

          /**
           * For custom elements or directives that may contain complex content that should be slotted into
           * the rendered element we use the `ssg-children` placeholder tag to act similar to a vue slot
           *
           * WARNING: We only support a single level of insertion. All of the children of the original element
           * will be inserted into the first ssg-children tag found.
           */
          if (el.node.type === 'element' && ssgSearchRoot?.type === 'element') {
            insertOriginalChildren(
              ssgSearchRoot.children as HastNode[],
              el.node.children ?? [],
            )
          }

          /**
           * Replace the original node with the newly created subtree
           *
           * objectReplace will remove any properties from the existing node
           * that are not defined in the new node
           */
          objectReplace(el.node, ssgSearchRoot)
        }),
      )
    }
  }

/**
 * Recursively search a Hast tree for the placeholder <ssg-children> element and
 * replace it with the originally rendered children
 */
function insertOriginalChildren(
  /** A list of children to search for the ssg-children tag in */
  children: HastNode[],
  /** The children of the original placeholder node */
  originalChildren: HastNode[],
) {
  let replacementIndex = -1
  children.forEach((child, idx) => {
    if (child.type === 'element') {
      // Only support the first ssg-children tag found
      if (replacementIndex !== -1) return

      if (child.tagName === 'ssg-children') {
        replacementIndex = idx
        return
      }

      if (child.children) {
        insertOriginalChildren(child.children, originalChildren)
      }
    }
  })

  if (replacementIndex >= 0) {
    children.splice(replacementIndex, 1, ...originalChildren)
  }
}

/** Add editor classnames to specific elements as needed */
export const addClassNames: () => Plugin<[], any, HastRoot> = () => () => {
  const tagClasses: Record<string, string> = {
    p: EditorClasses.Paragraph,
    blockquote: EditorClasses.Quote,
    hr: EditorClasses.LineBreak,
    ol: [EditorClasses.List, EditorClasses.Paragraph].join(' '),
    ul: [EditorClasses.List, EditorClasses.Paragraph].join(' '),
    // Inline styles
    strong: EditorClasses.Bold,
    b: EditorClasses.Bold,
    em: EditorClasses.Italic,
    code: EditorClasses.InlineCode,
    a: EditorClasses.Anchor,
  }

  return async (tree) => {
    visit(tree, (node) => {
      // If the node is an element type we check if it is a text extension placeholder tag
      if (node.type === 'element') {
        const className = tagClasses[node.tagName]
        if (className)
          node.properties = {
            ...node.properties,
            class: className,
          }
      }
    })
  }
}
